/**
 * Sidebar tab switching for mega menus, with overflow "View more" cue.
 */
class MegaMenuSidebar {
  /** @type {WeakSet<Element>} */
  static #initialized = new WeakSet();

  /**
   * @param {Element} layout
   */
  static init(layout) {
    if (MegaMenuSidebar.#initialized.has(layout)) return;
    MegaMenuSidebar.#initialized.add(layout);

    const items = layout.querySelectorAll('.mega-menu__sidebar-item[data-sidebar-index]');
    const panels = layout.querySelectorAll('.mega-menu__sidebar-panel');
    const panelsEl = layout.querySelector('[data-sidebar-panels]');
    const moreEl = layout.querySelector('[data-sidebar-more]');
    const wrapEl = layout.querySelector('.mega-menu__sidebar-panels-wrap');

    if (!items.length || !panels.length || !(panelsEl instanceof HTMLElement)) return;

    /**
     * @param {string} index
     */
    const activate = (index) => {
      items.forEach((item) => {
        const isActive = item.dataset.sidebarIndex === index;
        item.classList.toggle('mega-menu__sidebar-item--active', isActive);
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.panelIndex === index;
        panel.classList.toggle('mega-menu__sidebar-panel--active', isActive);
        panel.hidden = !isActive;
      });

      panelsEl.scrollTop = 0;
      requestAnimationFrame(updateMoreState);
    };

    /**
     * Show/hide the bottom "View more" fade based on remaining scroll.
     */
    const updateMoreState = () => {
      if (!(moreEl instanceof HTMLElement) || !(wrapEl instanceof HTMLElement)) return;

      const hasOverflow = panelsEl.scrollHeight > panelsEl.clientHeight + 1;
      const remaining = panelsEl.scrollHeight - panelsEl.scrollTop - panelsEl.clientHeight;
      const hasMore = hasOverflow && remaining > 4;

      wrapEl.toggleAttribute('data-has-more', hasMore);
      moreEl.hidden = !hasMore;
      moreEl.setAttribute('aria-hidden', hasMore ? 'false' : 'true');
    };

    items.forEach((item) => {
      item.addEventListener('pointerenter', () => {
        if (item.dataset.sidebarIndex) {
          activate(item.dataset.sidebarIndex);
        }
      });

      item.addEventListener('focus', () => {
        if (item.dataset.sidebarIndex) {
          activate(item.dataset.sidebarIndex);
        }
      });
    });

    panelsEl.addEventListener('scroll', updateMoreState, { passive: true });

    if (moreEl instanceof HTMLElement) {
      moreEl.addEventListener('pointerdown', (event) => {
        // Prevent focus steal — focusing this button blurs the menu link and closes the dropdown
        event.preventDefault();
        event.stopPropagation();
        panelsEl.scrollBy({
          top: Math.max(120, Math.floor(panelsEl.clientHeight * 0.6)),
          behavior: 'smooth',
        });
      });
    }

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => updateMoreState());
      resizeObserver.observe(panelsEl);
    }

    requestAnimationFrame(updateMoreState);
  }
}

function initMegaMenuSidebars() {
  document.querySelectorAll('.mega-menu__sidebar-layout').forEach((layout) => {
    MegaMenuSidebar.init(layout);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegaMenuSidebars);
} else {
  initMegaMenuSidebars();
}

document.addEventListener('shopify:section:load', initMegaMenuSidebars);
